import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { ReportView } from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { fetcher } from '../../../services/fetcher';
import { normalizePaginatedListResponse } from '../../../services/paginatedList';
import { WeeklySummaryReport, type WeeklySummaryReportData } from './WeeklySummaryReport';
import { useManagementCompanyLogoBlobUrl } from './useManagementCompanyLogo';

type ManagementCompanyRow = {
  id: number;
  name: string;
  isActive?: boolean;
  logoStorageKey?: string | null;
};

const WeeklySummaryReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [params] = useSearchParams();
  const vesselId = params.get('vesselId')?.trim() || '';
  const weekStart = params.get('weekStart')?.trim() || '';

  const key =
    vesselId && weekStart
      ? `/wrh/reports/weekly?vesselId=${encodeURIComponent(vesselId)}&weekStart=${encodeURIComponent(weekStart)}`
      : null;

  const { data, error, isLoading } = useSWR<WeeklySummaryReportData>(key, fetcher, {
    revalidateOnFocus: false,
  });
  const { data: companiesRaw } = useSWR('/management-companies?page=1&limit=50', fetcher, {
    revalidateOnFocus: false,
  });

  const managementCompany = useMemo(() => {
    const fromApi = data?.managementCompany;
    if (fromApi?.id && fromApi.logoStorageKey) {
      return {
        id: fromApi.id,
        name: fromApi.name,
        logoStorageKey: fromApi.logoStorageKey,
      };
    }
    const list = normalizePaginatedListResponse<ManagementCompanyRow>(companiesRaw, 1, 50).data;
    const active = list.filter((c) => c.isActive !== false);
    const withLogo = active.find((c) => c.logoStorageKey) ?? list.find((c) => c.logoStorageKey);
    if (withLogo?.logoStorageKey) {
      return {
        id: withLogo.id,
        name: fromApi?.name || withLogo.name,
        logoStorageKey: withLogo.logoStorageKey,
      };
    }
    return fromApi?.id
      ? { id: fromApi.id, name: fromApi.name, logoStorageKey: fromApi.logoStorageKey ?? null }
      : null;
  }, [data?.managementCompany, companiesRaw]);

  const { logoBlobUrl: companyLogoSrc } = useManagementCompanyLogoBlobUrl(
    managementCompany?.id,
    managementCompany?.logoStorageKey,
  );

  const backHref = useMemo(() => {
    if (!vesselId || !weekStart) return '/wrh/reports';
    return `/wrh/reports?vesselId=${encodeURIComponent(vesselId)}&weekStart=${encodeURIComponent(weekStart)}`;
  }, [vesselId, weekStart]);

  if (!vesselId || !weekStart) {
    return (
      <div className={cn('flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4', colors.background)}>
        <p className={cn('text-sm', colors.textMuted)}>Select vessel and week on the weekly report page first.</p>
        <Link to="/wrh/reports" className={cn('text-sm font-medium', colors.primaryFg, 'hover:underline')}>
          ← Back to weekly report
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cn('flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4', colors.background)}>
        <p className={cn('text-sm', colors.textMuted)}>Could not load weekly report.</p>
        <Link to={backHref} className={cn('text-sm font-medium', colors.primaryFg, 'hover:underline')}>
          ← Back to weekly report
        </Link>
      </div>
    );
  }

  const nc = (data.crew ?? []).filter((c) => c.anyNc).length;
  const reportData: WeeklySummaryReportData = {
    ...data,
    managementCompany,
  };

  return (
    <ReportView
      backTo={backHref}
      backLabel="Back to weekly report"
      title="WRH weekly summary"
      subtitle={
        <>
          {data.vesselName || 'Vessel'}
          {data.vesselAbrv ? ` (${data.vesselAbrv})` : ''} · {formatDateGlobal(data.weekStart)} –{' '}
          {formatDateGlobal(data.weekEnd)} · {(data.crew ?? []).length} crew
          {nc > 0 ? ` · ${nc} NC` : ''}
        </>
      }
      printPageSize="A4 landscape"
      printMargin="0"
    >
      <WeeklySummaryReport data={reportData} companyLogoSrc={companyLogoSrc} />
    </ReportView>
  );
};

export default WeeklySummaryReportPage;
